import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '@/api';
import { FormFieldLabel, FormInput } from '@/common/forms/FormInput';
import { InfoSectionContainer } from '@/common/InfoSection';
import { LargeArrowBackButton } from '@/common/LargeArrowBackButton';
import { WBSButton } from '@/common/WBSButton';
import {
  PEOPLE_PICKER_DEBOUNCE_MS,
  PEOPLE_PICKER_MIN_QUERY,
  PEOPLE_PICKER_PAGE_SIZE,
  type Person,
  useAsyncPeoplePicker,
} from '~/hooks/useAsyncPeoplePicker';
import { SearchSelect } from '~/styles/forms/SearchSelect';

export type MemberTagFormValues = {
  tagName: string;
  memberIds: string[];
};

type MemberTagFormProps = {
  groupId: number;
  submitLabel: string;
  initialTagName?: string;
  initialMembers?: Person[];
  isSubmitting?: boolean;
  onSubmit: (values: MemberTagFormValues) => void;
  onBack: () => void;
};

export function MemberTagForm({
  groupId,
  submitLabel,
  initialTagName = '',
  initialMembers,
  isSubmitting = false,
  onSubmit,
  onBack,
}: MemberTagFormProps) {
  const { t } = useTranslation();
  const [tagName, setTagName] = useState(initialTagName);
  // What's typed, and the debounced term the query is keyed on, so each
  // keystroke isn't its own request.
  const [term, setTerm] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setSearch(term), PEOPLE_PICKER_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [term]);

  // A server search over the group's students, the same population a tag's
  // member list shows. Legacy's picker also offered admins and mods, which its
  // list then hid, leaving invisible rows.
  //
  // On a group type with no Students tab this endpoint answers 403 and the
  // picker stays empty.
  const { data: matches, isFetching } = api.getGroupStudents.useQuery(
    {
      path: { group_id: groupId },
      query: { search, limit: PEOPLE_PICKER_PAGE_SIZE },
    },
    {
      enabled: groupId > 0 && search.length >= PEOPLE_PICKER_MIN_QUERY,
    },
  );

  const results = useMemo<Person[]>(
    () =>
      (matches?.data ?? []).map((student) => ({
        id: student.id,
        name: student.display_name,
      })),
    [matches],
  );

  // Stay loading through the debounce window too, so "no results" doesn't flash
  // between a keystroke and its request.
  const isSearching = term.length >= PEOPLE_PICKER_MIN_QUERY;
  const membersPicker = useAsyncPeoplePicker(
    {
      results,
      loading: isSearching && (term !== search || isFetching),
      onSearch: setTerm,
    },
    true,
  );
  const { preselect } = membersPicker;

  useEffect(() => {
    if (!initialMembers) return;
    for (const m of initialMembers) {
      preselect(m);
    }
  }, [initialMembers, preselect]);

  return (
    <InfoBox>
      <LargeArrowBackButton onClick={onBack} />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit({ tagName, memberIds: membersPicker.value });
        }}
      >
        <FormInput
          alternate
          required
          hideRequiredLabel
          inputStyle={{ backgroundColor: 'var(--grey-75)' }}
          label={t('wbs.tag-name')}
          value={tagName}
          onChange={setTagName}
        />
        <Field>
          <FormFieldLabel>{t('profile.members')}</FormFieldLabel>
          <SearchSelect
            multi
            loading={membersPicker.loading}
            minQueryLength={PEOPLE_PICKER_MIN_QUERY}
            options={membersPicker.options}
            value={membersPicker.value}
            onChange={membersPicker.onChange}
            onSearch={membersPicker.onSearch}
          />
        </Field>
        <Footer>
          <WBSButton type="submit" wide isLoading={isSubmitting}>
            {submitLabel}
          </WBSButton>
        </Footer>
      </form>
    </InfoBox>
  );
}

const InfoBox = styled(InfoSectionContainer)`
  position: relative;
`;

const Field = styled.div`
  margin-bottom: 24px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
`;
